import { useAuthStore } from '@/store/authStore';

/**
 * Hook to check user permissions for UI elements
 */
export function usePermissions() {
  const { user } = useAuthStore();
  const userPermissions = user?.permissions || [];

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: string): boolean => {
    return userPermissions.includes(permission);
  };

  /**
   * Check if user has any of the specified permissions (OR logic)
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(perm => userPermissions.includes(perm));
  };

  /**
   * Check if user has all of the specified permissions (AND logic)
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every(perm => userPermissions.includes(perm));
  };

  /**
   * Module-specific permission checks
   */
  const canViewModule = (moduleName: string): boolean => {
    return hasPermission(`${moduleName}_view`);
  };

  const canEditModule = (moduleName: string): boolean => {
    return hasPermission(`${moduleName}_edit`);
  };

  const canDeleteModule = (moduleName: string): boolean => {
    return hasPermission(`${moduleName}_delete`);
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canViewModule,
    canEditModule,
    canDeleteModule,
    userPermissions,
  };
}
